import React from "react";
import { getBlockMeta } from "../flowBuilderUtils";
import type { FlowStep } from "../types";

type Props = {
  flowSteps: FlowStep[];
  selectedStepId: string;
  setSelectedStepId: (value: string) => void;
  addBlock: (block: string) => void;
  openMapper: () => void;
  openPayloadEditor: () => void;
  payloadIsValid?: boolean;
  payloadVariableCount?: number;
  missingRequiredCount?: number;
};

function isTriggerStep(step: FlowStep) {
  return step.actionType.startsWith("trigger.");
}

function Badge({
  children,
  tone = "slate",
}: {
  children: React.ReactNode;
  tone?: "slate" | "blue" | "green" | "orange";
}) {
  const classes = {
    slate:
      "border-slate-200 bg-slate-50 text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300",
    blue:
      "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-300",
    green:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
    orange:
      "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-wide ${classes[tone]}`}
    >
      {children}
    </span>
  );
}

export default function LogicAppFlowCanvas({
  flowSteps,
  selectedStepId,
  setSelectedStepId,
  addBlock,
  openMapper,
  openPayloadEditor,
  payloadIsValid = true,
  payloadVariableCount = 0,
  missingRequiredCount = 0,
}: Props) {
  return (
    <div className="relative min-h-[430px] overflow-hidden rounded-2xl bg-slate-50 dark:bg-slate-950">
      <div className="absolute inset-0 opacity-60 [background-image:radial-gradient(rgba(100,116,139,0.28)_1px,transparent_1px)] [background-size:18px_18px] dark:opacity-25 dark:[background-image:radial-gradient(rgba(255,255,255,0.18)_1px,transparent_1px)]" />
      <div className="absolute bottom-12 left-1/2 top-10 w-px -translate-x-1/2 bg-slate-200 dark:bg-white/10" />

      <div className="relative mx-auto flex max-w-6xl flex-col items-center px-6 py-8">
        {flowSteps.map((step, index) => {
          const selected = selectedStepId === step.id;
          const meta = getBlockMeta(step.title);
          const trigger = index === 0 && isTriggerStep(step);
          const booking = step.actionType === "jdx.create-shipment-booking";

          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                onClick={() => setSelectedStepId(step.id)}
                className={`relative z-10 w-full rounded-2xl border bg-white text-left shadow-sm transition hover:border-orange-300 hover:shadow-md dark:bg-slate-900/90 ${
                  selected
                    ? "border-orange-400 ring-4 ring-orange-500/10"
                    : trigger
                      ? "border-cyan-300 dark:border-cyan-500/30"
                      : "border-slate-200 dark:border-white/10"
                }`}
              >
                <div className="flex min-h-[74px] items-center gap-4 px-5 py-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-black ${
                      trigger
                        ? "bg-cyan-500 text-white"
                        : "bg-orange-500 text-white"
                    }`}
                  >
                    {meta.icon}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-black text-slate-950 dark:text-white">
                        {step.title}
                      </h3>

                      {trigger && <Badge tone="blue">Start</Badge>}
                    </div>

                    <p className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
                      {step.subtitle}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {step.actionType === "trigger.http" && (
                      <>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openPayloadEditor();
                          }}
                          className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-black text-white shadow-sm transition hover:bg-orange-400"
                        >
                          Payload
                        </button>

                        <Badge tone={payloadIsValid ? "green" : "orange"}>
                          {payloadIsValid ? "Valid" : "Invalid"}
                        </Badge>
                      </>
                    )}

                    {booking && (
                      <>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openMapper();
                          }}
                          className="rounded-xl bg-orange-500 px-4 py-2 text-xs font-black text-white shadow-sm transition hover:bg-orange-400"
                        >
                          Mapping
                        </button>

                        <Badge tone={missingRequiredCount === 0 ? "green" : "orange"}>
                          {missingRequiredCount === 0
                            ? "Ready"
                            : `${missingRequiredCount} missing`}
                        </Badge>
                      </>
                    )}

                    {trigger && <Badge>Locked</Badge>}
                    {!trigger && !booking && <Badge tone="green">Active</Badge>}

                    <span className="px-1 text-slate-300">⋮</span>
                  </div>
                </div>
              </button>

              {index !== flowSteps.length - 1 && (
                <div className="relative z-10 flex h-11 items-center justify-center">
                  <button
                    type="button"
                    onClick={() => addBlock("Condition")}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-orange-400 hover:bg-orange-500 hover:text-white dark:border-white/10 dark:bg-slate-900 dark:text-slate-300"
                  >
                    +
                  </button>
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className="relative z-10 mt-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-[10px] font-black uppercase tracking-wide text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
          End workflow
        </div>
      </div>
    </div>
  );
}